import { put, call, takeLatest, select } from "redux-saga/effects";

import { ApiService, Cases, Slide } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { Annotation, ServerAnnotation } from "custom-models/ViewerTypes";

import { SysState } from "reducers/sysReducer";

import {
  ADD_ANNOTATION_FAILURE,
  ADD_ANNOTATION_REQUEST,
  ADD_ANNOTATION_SUCCESS,
  CASE_BY_ACCESSION_FAILURE,
  CASE_BY_ACCESSION_REQUEST,
  CASE_BY_ACCESSION_SUCCESS,
  DELETE_ANNOTATION_FAILURE,
  DELETE_ANNOTATION_REQUEST,
  DELETE_ANNOTATION_SUCCESS,
  EDIT_ANNOTATION_FAILURE,
  EDIT_ANNOTATION_REQUEST,
  EDIT_ANNOTATION_SUCCESS,
  FETCH_ANNOTATIONS_FAILURE,
  FETCH_ANNOTATIONS_REQUEST,
  FETCH_ANNOTATIONS_SUCCESS,
  SELECT_ANNOTATION,
  SLIDE_BARCODE_URL_FAILURE,
  SLIDE_BARCODE_URL_REQUEST,
  SLIDE_BARCODE_URL_SUCCESS,
  SLIDE_DETAIL_FAILURE,
  SLIDE_DETAIL_REQUEST,
  SLIDE_DETAIL_SUCCESS,
  SLIDE_MACRO_URL_FAILURE,
  SLIDE_MACRO_URL_REQUEST,
  SLIDE_MACRO_URL_SUCCESS,
  SLIDE_REQUEST_RESCAN_FAILURE,
  SLIDE_REQUEST_RESCAN_REQUEST,
  SLIDE_REQUEST_RESCAN_SUCCESS,
} from "../actions/constants";
import { showMask } from "../actions/slideDetailActions";
import {
  parseAnnotationRecord,
  toAnnotationRecord,
} from "../utils/annotations";

// If they select a ROI annotation, switch to its corresponding lesion line
function* selectMask(action: any): any {
  if (action.payload.annotation?.lesionLineType) {
    yield put(showMask(action.payload.annotation.lesionLineType));
  }
}

function* requestSlideDetail(action: any): any {
  try {
    const payload: Slide = yield call(ApiService.retrieveSlide, action.payload);
    const canViewNonSuccessSlides: boolean | undefined = yield select(
      ({ sys }: { sys: SysState }) => sys?.features?.canViewNonSuccessSlides
    );
    yield put({
      type: SLIDE_DETAIL_SUCCESS,
      payload,
      _meta: { canViewNonSuccessSlides },
    });
  } catch (e) {
    yield put({ type: SLIDE_DETAIL_FAILURE, error: true, payload: e });
  }
}

function* fetchBarcodeUrl(action: any): any {
  if (action.payload.slideId) {
    try {
      const response: { url: string } = yield call(ApiService.barcodeUrlSlide, {
        id: action.payload.slideId,
      });
      yield put({
        type: SLIDE_BARCODE_URL_SUCCESS,
        payload: {
          location: response.url || "MISSING",
          slide_id: action.payload.slideId,
        },
      });
    } catch (error: unknown) {
      const e = error as ApiError;
      if (e.status === 404) {
        yield put({
          type: SLIDE_BARCODE_URL_SUCCESS,
          payload: { location: "MISSING", slide_id: action.payload.slideId },
        });
      } else {
        yield put({ type: SLIDE_BARCODE_URL_FAILURE, error: true, payload: e });
      }
    }
  }
}

function* fetchMacroUrl(action: any): any {
  if (action.payload.slideId) {
    try {
      const response: { url: string } = yield call(ApiService.macroUrlSlide, {
        id: action.payload.slideId,
      });
      yield put({
        type: SLIDE_MACRO_URL_SUCCESS,
        payload: {
          location: response.url || "MISSING",
          slide_id: action.payload.slideId,
        },
      });
    } catch (error: unknown) {
      const e = error as ApiError;
      if (e.status === 404) {
        yield put({
          type: SLIDE_MACRO_URL_SUCCESS,
          payload: { location: "MISSING", slide_id: action.payload.slideId },
        });
      } else {
        yield put({ type: SLIDE_MACRO_URL_FAILURE, error: true, payload: e });
      }
    }
  }
}

function* fetchCaseByAccession(action: any): any {
  if (action.payload.accession_id) {
    const { accession_id } = action.payload;
    try {
      const responseBody = yield call(ApiService.listCases, {
        accession: accession_id,
      });
      let match: Cases | undefined = undefined;
      let noMatchingCaseByAccessionResult = true;
      responseBody.results?.forEach((singleResult: Cases) => {
        if (
          singleResult.accession?.toUpperCase() === accession_id.toUpperCase()
        ) {
          match = singleResult;
          noMatchingCaseByAccessionResult = false;
        }
      });
      yield put({
        type: CASE_BY_ACCESSION_SUCCESS,
        payload: {
          caseByAccessionResult: match,
          noMatchingCaseByAccessionResult,
        },
      });
    } catch (e) {
      yield put({ type: CASE_BY_ACCESSION_FAILURE, error: true, payload: e });
    }
  }
}

function* fetchAnnotations(action: any): any {
  try {
    const paginationResult = yield call(ApiService.listSlideAnnotations, {
      parentLookupSlideId: action.payload.slideId,
    });
    const annotations: Annotation[] = (paginationResult || []).map(
      (result: ServerAnnotation) => parseAnnotationRecord(result)
    );
    yield put({ type: FETCH_ANNOTATIONS_SUCCESS, payload: { annotations } });
  } catch (e) {
    yield put({ type: FETCH_ANNOTATIONS_FAILURE, error: true, payload: e });
  }
}

function* addAnnotation(action: any): any {
  const record = toAnnotationRecord(action.payload.annotation);
  try {
    //@ts-ignore createSlideAnnotation OpenAPI signature is broken (doesn't take array)
    const slideAnnotation = yield call(ApiService.createSlideAnnotation, {
      parentLookupSlideId: action.payload.slideId,
      requestBody: [record],
    });
    const annotation = {
      ...action.payload.annotation,
      id: slideAnnotation[0].id,
    };
    yield put({
      type: ADD_ANNOTATION_SUCCESS,
      payload: { annotation },
    });
  } catch (e) {
    yield put({ type: ADD_ANNOTATION_FAILURE, error: true, payload: e });
  }
}

function* deleteAnnotation(action: any): any {
  try {
    yield call(ApiService.destroySlideAnnotation, {
      parentLookupSlideId: action.payload.slideId,
      id: action.payload.annotationId,
    });
    yield put({
      type: DELETE_ANNOTATION_SUCCESS,
      payload: { annotationId: action.payload.annotationId },
    });
  } catch (e) {
    yield put({
      type: DELETE_ANNOTATION_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* editAnnotation(action: any): any {
  const { slideId, annotation, requestBody } = action.payload;
  try {
    const result = yield call(ApiService.partialUpdateSlideAnnotation, {
      parentLookupSlideId: slideId,
      id: annotation?.id,
      requestBody,
    });
    const finalForm = {
      ...annotation,
      ...result, // ...requestBody?
    };
    yield put({
      type: EDIT_ANNOTATION_SUCCESS,
      payload: { slideId, annotation: finalForm },
    });
  } catch (e) {
    yield put({ type: EDIT_ANNOTATION_FAILURE, error: true, payload: e });
  }
}

function* createRescanRequest(action: any) {
  try {
    yield call(ApiService.createRescanRequestLog, action.payload);
    yield put({
      type: SLIDE_REQUEST_RESCAN_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: SLIDE_REQUEST_RESCAN_FAILURE,
      error: true,
      payload: e,
    });
  }
}

export function* slideDetailSaga() {
  yield takeLatest(SLIDE_DETAIL_REQUEST, requestSlideDetail);
  yield takeLatest(SLIDE_BARCODE_URL_REQUEST, fetchBarcodeUrl);
  yield takeLatest(SLIDE_MACRO_URL_REQUEST, fetchMacroUrl);
  yield takeLatest(CASE_BY_ACCESSION_REQUEST, fetchCaseByAccession);
  yield takeLatest(FETCH_ANNOTATIONS_REQUEST, fetchAnnotations);
  yield takeLatest(ADD_ANNOTATION_REQUEST, addAnnotation);
  yield takeLatest(DELETE_ANNOTATION_REQUEST, deleteAnnotation);
  yield takeLatest(EDIT_ANNOTATION_REQUEST, editAnnotation);
  yield takeLatest(SELECT_ANNOTATION, selectMask);
  yield takeLatest(SLIDE_REQUEST_RESCAN_REQUEST, createRescanRequest);
}
